import Axios, { AxiosResponse } from 'axios';
import PropTypes from 'prop-types';
import qs from 'query-string';
import { Action } from 'redux';
import { AuthWrapperDecorator } from 'redux-auth-wrapper';
import { connectedRouterRedirect, InjectedAuthRouterProps } from 'redux-auth-wrapper/history4/redirect';
import { ThunkAction, ThunkDispatch } from 'redux-thunk';
import Cookies, { CookieSetOptions } from 'universal-cookie';
import { config as PrimeConfig } from '../../../prime/common';
import { ConfigBase } from '../../types/config';
import { SurveyFollowupInputDTO } from '../../types/dto/SurveyFollowupInputDTO';
import { Utils } from '../../utils/';
import ApiHelper from '../ApiHelper';
import * as Actions from './Actions';
import { AuthError, AuthErrorInfo, AuthState, TokenSetWithExpire } from './Types';

type RootSubState = { authState: AuthState };
type AuthThunkAction<R> = ThunkAction<R, RootSubState, undefined, Action>;
type AuthThunkDispatch = ThunkDispatch<RootSubState, undefined, Action>;

export type ServiceActions = {
    removeTokensFromSessionStorage: () => void,
    addTokensToSessionStorage: (data: TokenSetWithExpire) => void,
    getActualAccessToken: () => string,
    loginUser: (code: string, state: string) => Promise<void>,
    useRefreshToken: () => Promise<void>,
    surveyFollowup: (surveyFollowupInputDto: SurveyFollowupInputDTO) => Promise<void>,
    logout: () => Promise<void>,
    logoutDueToNewAccessTokenFailure: (error: AuthErrorInfo) => Promise<void>,
    requireAuthentication: AuthWrapperDecorator<InjectedAuthRouterProps>,
};

export const ServiceActionPropTypes = PropTypes.shape({
    removeTokensFromSessionStorage: PropTypes.func.isRequired,
    addTokensToSessionStorage: PropTypes.func.isRequired,
    getActualAccessToken: PropTypes.func.isRequired,
    loginUser: PropTypes.func.isRequired,
    useRefreshToken: PropTypes.func.isRequired,
    surveyFollowup: PropTypes.func.isRequired,
    logout: PropTypes.func.isRequired,
    logoutDueToNewAccessTokenFailure: PropTypes.func.isRequired,
    requireAuthentication: PropTypes.func.isRequired,
}).isRequired;

export const GetActualAccessToken: () => string | null | undefined = () => {
    if (PrimeConfig.UseAuthCookie === true) {
        const cookies = new Cookies();
        return cookies.get(PrimeConfig.AccessTokenAlias);
    } else {
        return sessionStorage.getItem(PrimeConfig.AccessTokenAlias);
    }
};

export const createServiceActions = (config: ConfigBase) => {
    const apiHelper = ApiHelper(config);
    const removeTokensFromSessionStorage = () => {
        if (PrimeConfig.UseAuthCookie === true) {
            // Apr 2020: AT stored on a cookie
            const cookies = new Cookies();
            cookies.remove(config.AccessTokenAlias, { path: '/' });
        } else {
            sessionStorage.removeItem(config.AccessTokenAlias);
        }
    };

    const addTokensToSessionStorage = (data: TokenSetWithExpire) => {
        removeTokensFromSessionStorage();
        if (PrimeConfig.UseAuthCookie === true) {
            // Apr 2020: AT stored on a cookie
            const cookies = new Cookies();
            const expirationDate = new Date();
            expirationDate.setSeconds(expirationDate.getSeconds() + PrimeConfig.ATCookieTimeToLive);
            const cookieOptions: CookieSetOptions = {
                path: '/',
                expires: expirationDate,
            };
            if (PrimeConfig.ATCookieSameSite) {
                cookieOptions.sameSite = 'none';
                cookieOptions.secure = true;
            } else {
                cookieOptions.sameSite = 'lax';
                cookieOptions.secure = false;
            }
            cookies.set(config.AccessTokenAlias, data.accessToken, cookieOptions);
        } else {
            sessionStorage.setItem(config.AccessTokenAlias, data.accessToken);
        }
    };

    const getActualAccessToken = () => {
        if (PrimeConfig.UseAuthCookie === true) {
            const cookies = new Cookies();
            return cookies.get(config.AccessTokenAlias);
        } else {
            return sessionStorage.getItem(PrimeConfig.AccessTokenAlias);
        }
    };

    const loginUserSuccess = (dispatch: AuthThunkDispatch, data: TokenSetWithExpire) => {
        addTokensToSessionStorage(data);
        dispatch(Actions.loginUserSuccess(data));
    };

    const loginUserFailure = (dispatch: AuthThunkDispatch, error: AuthErrorInfo) => {
        removeTokensFromSessionStorage();
        dispatch(Actions.loginUserFailure(error));
    };

    const getnewAccessTokenSuccess = (dispatch: AuthThunkDispatch, data: TokenSetWithExpire) => {
        addTokensToSessionStorage(data);
        dispatch(Actions.getnewAccessTokenSuccess(data));
    };

    const getnewAccessTokenFailure = (dispatch: AuthThunkDispatch, error: AuthErrorInfo) => {
        removeTokensFromSessionStorage();
        dispatch(Actions.getnewAccessTokenFailure(error));
    };

    const loginUser = (code: string, state: string): AuthThunkAction<Promise<void>> => {
        return async (dispatch) => {
            dispatch(Actions.loginUserRequest());
            try {
                const response: AxiosResponse<TokenSetWithExpire> = await Axios.post(
                    `${config.ApiServerHostName}${config.ApiURL}/api/prime/app/get-access-token`,
                    qs.stringify({
                        code,
                        state,
                    }),
                    {
                        headers: {
                            accept: 'application/json',
                        },
                    },
                );
                Utils.checkHttpStatus(response);
                loginUserSuccess(dispatch, response.data);
            } catch (error) {
                dispatch(apiHelper.logFrontendError(error));
                try {
                    const anyError = error as any;
                    const authErrorInfo: AuthErrorInfo = {
                        response: anyError?.response,
                        errorCode: config.Constants.ErrorCodes.UnknownError,
                        errorMessage: null,
                        exceptionType: null,
                        exceptionData: null,
                    };

                    if (Axios.isAxiosError(error) && error.response) {
                        if (error.response.status === 400 && error.response.data !== null && error.response.data.error === 'invalid_grant') {
                            switch (error.response.data.error_description) {
                                case 'invalid_user_or_password':
                                    authErrorInfo.errorCode = config.Constants.ErrorCodes.InvalidUsernameOrPassword;
                                    break;
                                default:
                                    authErrorInfo.errorCode = config.Constants.ErrorCodes.AuthenticationFailed;
                                    break;
                            }

                            authErrorInfo.errorMessage = error.response.data.error_description;
                        } else {
                            authErrorInfo.errorMessage = `${error.response.status} ${error.response.statusText}`;
                            authErrorInfo.exceptionType = error.response.data.ExceptionType;
                            authErrorInfo.exceptionData = `${error.response.data.ExceptionMessage}`;
                        }
                    } else {
                        authErrorInfo.errorMessage = anyError?.message;
                    }

                    loginUserFailure(dispatch, authErrorInfo);
                    throw new AuthError(authErrorInfo);
                } catch (subError) {
                    if (subError instanceof AuthError) {
                        throw subError;
                    } else {
                        throw error;
                    }
                }
            }
        };
    };

    const useRefreshToken = (): AuthThunkAction<Promise<void>> => {
        return (dispatch) => {
            return new Promise((resolve, reject) => {
                dispatch(Actions.getnewAccessTokenRequest());
                // Apr 2020: AT stored on a cookie
                // const accessToken = sessionStorage.getItem(config.AccessTokenAlias);
                const accessToken = getActualAccessToken();
                const authErrorInfoBase: AuthErrorInfo = {
                    response: null,
                    errorCode: config.Constants.ErrorCodes.AuthenticationFailed,
                    errorMessage: null,
                    exceptionType: null,
                    exceptionData: null,
                };
                if (accessToken) {
                    Axios.post(
                        `${config.ApiServerHostName}${config.ApiURL}/api/prime/app/refresh-access-token`,
                        qs.stringify({
                            accessToken,
                        }),
                        {
                            headers: {
                                accept: 'application/json',
                            },
                        },
                    )
                        .then(Utils.checkHttpStatus)
                        .then((response: AxiosResponse<TokenSetWithExpire>) => {
                            getnewAccessTokenSuccess(dispatch, response.data);
                            resolve();
                        }).catch((error: any) => {
                            const authErrorInfo: AuthErrorInfo = {
                                response: error.response,
                                errorCode: config.Constants.ErrorCodes.AuthenticationFailed,
                                errorMessage: null,
                                exceptionType: null,
                                exceptionData: null,
                            };
                            if (error.response) {
                                if (error.response.status === 400 && error.response.data !== null && error.response.data.error === 'invalid_grant') {
                                    authErrorInfo.errorCode = config.Constants.ErrorCodes.SessionExpired;
                                } else {
                                    authErrorInfo.errorMessage += `${error.response.status} ${error.response.statusText}`;
                                }
                            } else {
                                authErrorInfo.errorMessage += `${error.message}`;
                            }

                            getnewAccessTokenFailure(dispatch, authErrorInfo);
                            reject(authErrorInfo);
                        });
                } else {
                    getnewAccessTokenFailure(dispatch, authErrorInfoBase);
                    reject(new AuthError(authErrorInfoBase));
                }
            });
        };
    };

    const surveyFollowup = (surveyFollowupInputDto: SurveyFollowupInputDTO): AuthThunkAction<Promise<void>> => {
        return async (dispatch) => {
            dispatch(Actions.surveyFollowupRequest());
            try {
                const response: AxiosResponse<void> = await Axios.post(
                    `${config.ApiServerHostName}${config.ApiURL}/api/prime/users/survey-followup`,
                    surveyFollowupInputDto,
                    {
                        headers: {
                            accept: 'application/json',
                        },
                    },
                );
                Utils.checkHttpStatus(response);
                dispatch(Actions.surveyFollowupSuccess());
            } catch (error) {
                dispatch(apiHelper.logFrontendError(error));
                try {
                    const anyError = error as any;
                    const authErrorInfo: AuthErrorInfo = {
                        response: anyError?.response,
                        errorCode: config.Constants.ErrorCodes.UnknownError,
                        errorMessage: null,
                        exceptionType: null,
                        exceptionData: null,
                    };

                    authErrorInfo.errorCode = config.Constants.ErrorCodes.UnknownError;
                    if (Axios.isAxiosError(error) && error.response) {
                        authErrorInfo.errorMessage = `${error.response.status} ${error.response.statusText}`;
                        authErrorInfo.exceptionType = error.response.data.ExceptionType;
                    } else {
                        authErrorInfo.errorMessage = anyError?.message;
                    }
                    dispatch(Actions.surveyFollowupFailure(authErrorInfo));
                    throw new AuthError(authErrorInfo);
                } catch (subError) {
                    if (subError instanceof AuthError) {
                        throw subError;
                    } else {
                        throw error;
                    }
                }
            }
        };
    };

    const logout = (): AuthThunkAction<Promise<void>> => {
        return (dispatch) => {
            removeTokensFromSessionStorage();
            dispatch(Actions.logoutUserSuccess());
            return new Promise<void>((resolve) => resolve());
        };
    };

    const logoutDueToNewAccessTokenFailure = (error: AuthErrorInfo): AuthThunkAction<Promise<void>> => {
        return (dispatch) => {
            removeTokensFromSessionStorage();
            dispatch(Actions.useNewAccessTokenFailure(error));
            return new Promise<void>((resolve) => resolve());
        };
    };

    const requireAuthentication = connectedRouterRedirect({
        redirectPath: () => {
            if (window.location.search) {
                return `${config.BaseURL}/login${window.location.search}`;
            } else {
                return `${config.BaseURL}/login`;
            }
        },   // should redirect to Info Portal?
        authenticatedSelector: (state: RootSubState) => state.authState.isAuthenticated,
        wrapperDisplayName: 'UserIsJWTAuthenticated',
    });

    return {
        removeTokensFromSessionStorage,
        addTokensToSessionStorage,
        getActualAccessToken,
        loginUser,
        useRefreshToken,
        surveyFollowup,
        logout,
        logoutDueToNewAccessTokenFailure,
        requireAuthentication,
    };
};

export const bindServiceActionCreators = (config: ConfigBase, dispatch: AuthThunkDispatch): ServiceActions => {
    const services = createServiceActions(config);
    return {
        removeTokensFromSessionStorage: services.removeTokensFromSessionStorage,
        addTokensToSessionStorage: services.addTokensToSessionStorage,
        getActualAccessToken: services.getActualAccessToken,
        loginUser: (code: string, state: string) => dispatch(services.loginUser(code, state)),
        useRefreshToken: () => dispatch(services.useRefreshToken()),
        surveyFollowup: (surveyFollowupInputDto: SurveyFollowupInputDTO) => dispatch(services.surveyFollowup(surveyFollowupInputDto)),
        logout: () => dispatch(services.logout()),
        logoutDueToNewAccessTokenFailure: (error: AuthErrorInfo) => dispatch(services.logoutDueToNewAccessTokenFailure(error)),
        requireAuthentication: services.requireAuthentication,
    };
};
